import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import { useDispatch, useSelector } from "react-redux";
import theme from "../../theme";
import CommonInput from "../input";
import CommonButton from "../button";
import Loading from "../loading";
import VendorProfileUpdateModal from "./vendorProfileUpdateModal";
import {
  getVendorProfileAction,
  getVendorQRCodeAction,
  updateDiscountPercentageAction,
  downloadQRCodeAction,
} from "../../containers/vendorDashboard/action";
import { moneyValue } from "../../utils";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.colors.White,
    width: "100%",
    minHeight: "calc(100vh - 185px)",
    padding: "45px 90px",
    marginLeft: "220px",
  },
  headerContainer: {
    marginBottom: "30px",
    "& .title": {
      color: theme.colors.Violet,
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "24px",
    },
  },
  card: {
    backgroundColor: theme.colors.White,
    boxShadow: "0px 2px 15px #D0CFDB99",
    borderRadius: "15px",
    padding: "30px 40px",
    marginBottom: "20px",
    "& .cardTitle": {
      color: theme.colors.Violet,
      fontSize: "18px",
      fontFamily: theme.fontFamily.MARKPROBOLD,
      marginBottom: "20px",
    },
    "& .label": {
      color: theme.colors.Violet,
      fontSize: "14px",
      fontFamily: theme.fontFamily.MARKPRO,
      marginBottom: "8px",
    },
    "& .value": {
      color: theme.colors.Violet,
      fontSize: "16px",
      fontFamily: theme.fontFamily.MARKPROHEAVY,
      marginBottom: "15px",
    },
  },
  qrCodeContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "& .qrCode": {
      width: "250px",
      height: "250px",
      border: `2px solid ${theme.colors.Zumthor}`,
      borderRadius: "10px",
      padding: "20px",
      backgroundColor: theme.colors.White,
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& img": {
        maxWidth: "100%",
        maxHeight: "100%",
      },
    },
    "& .buttonGroup": {
      display: "flex",
      gap: "15px",
    },
  },
  statusBadge: {
    display: "inline-block",
    padding: "5px 15px",
    borderRadius: "20px",
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    textTransform: "uppercase",
  },
  statusPending: {
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.DodgerBlue,
  },
  statusApproved: {
    backgroundColor: theme.colors.MintGreen,
    color: theme.colors.Violet,
  },
  statusRejected: {
    backgroundColor: theme.colors.Cinderella,
    color: theme.colors.Monza,
  },
}));

function VendorSettings() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const getVendorProfileMethod = useCallback(
    () => dispatch(getVendorProfileAction()),
    [dispatch]
  );

  const getVendorQRCodeMethod = useCallback(
    () => dispatch(getVendorQRCodeAction()),
    [dispatch]
  );

  const updateDiscountPercentageMethod = useCallback(
    (data) => dispatch(updateDiscountPercentageAction(data)),
    [dispatch]
  );

  const downloadQRCodeMethod = useCallback(
    () => dispatch(downloadQRCodeAction()),
    [dispatch]
  );

  const vendorDashboardReducer = useSelector((state) => state.vendorDashboardReducer);

  const [formData, setFormData] = useState({
    discountPercentage: "",
    isEditingDiscount: false,
  });

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  useEffect(() => {
    getVendorProfileMethod();
  }, [getVendorProfileMethod]);

  useEffect(() => {
    const profile = vendorDashboardReducer?.vendorProfile;
    if (profile && profile.discount_percentage !== undefined) {
      setFormData((prev) => ({ ...prev, discountPercentage: profile.discount_percentage }));
    }
  }, [vendorDashboardReducer?.vendorProfile]);

  useEffect(() => {
    if (vendorDashboardReducer?.updateDiscountSuccess) {
      setFormData((prev) => ({ ...prev, isEditingDiscount: false }));
      getVendorProfileMethod();
    }
  }, [vendorDashboardReducer?.updateDiscountSuccess, getVendorProfileMethod]);

  const handleUpdateDiscount = () => {
    if (formData.discountPercentage && !isNaN(formData.discountPercentage) && formData.discountPercentage >= 0 && formData.discountPercentage <= 100) {
      updateDiscountPercentageMethod({ discount_percentage: parseFloat(formData.discountPercentage) });
    }
  };

  const handleDownloadQR = () => {
    const qrCode = vendorDashboardReducer?.vendorProfile?.vendor?.qr_code_image;
    if (qrCode) {
      const base64Data = qrCode.includes(',') ? qrCode.split(',')[1] : qrCode;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const profile = vendorDashboardReducer?.vendorProfile?.vendor;
      link.download = `QRCode-${profile?.business_name || 'Sanaam'}-${new Date().getTime()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const handlePrintQR = () => {
    const qrCode = vendorDashboardReducer?.vendorProfile?.vendor?.qr_code_image;
    const profile = vendorDashboardReducer?.vendorProfile?.vendor;
    if (qrCode) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - ${profile?.business_name || "Sanaam"}</title>
            <style>
              body { 
                display: flex; 
                flex-direction: column; 
                align-items: center; 
                justify-content: center; 
                height: 100vh; 
                margin: 0;
                font-family: Arial, sans-serif;
              }
              img { max-width: 400px; max-height: 400px; }
              h2 { margin-top: 20px; }
            </style>
          </head>
          <body>
            <img src="${qrCode}" alt="QR Code" />
            <h2>${profile?.business_name || "Sanaam Merchant"}</h2>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return classes.statusApproved;
      case "rejected":
        return classes.statusRejected;
      default:
        return classes.statusPending;
    }
  };

  const profile = vendorDashboardReducer?.vendorProfile?.vendor;
  const qrCode = vendorDashboardReducer?.vendorProfile?.vendor?.qr_code_image;

  return (
    <div className={classes.root}>
      <Loading showLoader={vendorDashboardReducer?.loading} />
      {vendorDashboardReducer?.error?.length ? (
        <div className={"error-container"}>
          {Array.isArray(vendorDashboardReducer.error)
            ? vendorDashboardReducer.error.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : <div>{vendorDashboardReducer.error}</div>}
        </div>
      ) : null}
      {vendorDashboardReducer?.updateDiscountError?.length ? (
        <div className={"error-container"}>
          {Array.isArray(vendorDashboardReducer.updateDiscountError)
            ? vendorDashboardReducer.updateDiscountError.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : <div>{vendorDashboardReducer.updateDiscountError}</div>}
        </div>
      ) : null}
      {vendorDashboardReducer?.updateVendorProfileError?.length ? (
        <div className={"error-container"}>
          {Array.isArray(vendorDashboardReducer.updateVendorProfileError)
            ? vendorDashboardReducer.updateVendorProfileError.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : <div>{vendorDashboardReducer.updateVendorProfileError}</div>}
        </div>
      ) : null}

      <div className={classes.headerContainer}>
        <div className="title">Settings</div>
      </div>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <div className={classes.card}>
            <div className="cardTitle">Profile Information</div>
            <div className="label">First Name</div>
            <div className="value">{vendorDashboardReducer?.vendorProfile?.user?.first_name || "N/A"}</div>

            <div className="label">Last Name</div>
            <div className="value">{vendorDashboardReducer?.vendorProfile?.user?.last_name || "N/A"}</div>


            <div className="label">Phone Number</div>
            <div className="value">{profile?.phone_number || "N/A"}</div>

            <CommonButton
              text="Update Profile"
              stylevariant="primary"
              onClick={() => setIsUpdateModalOpen(true)}
              style={{ marginTop: "20px" }}
            />
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className={classes.card}>
            <div className="cardTitle">Shop Information</div>
            <div className="label">Shop Name</div>
            <div className="value">{profile?.business_name || "N/A"}</div>

            <div className="label">Location</div>
            <div className="value">{profile?.address || "N/A"}</div>

            <div className="label">Email</div>
            <div className="value">{profile?.owner_email || "N/A"}</div>

            <div className="label">Account Status</div>
            <div>
              <span
                className={`${classes.statusBadge} ${getStatusBadgeClass(
                  profile?.status
                )}`}
              >
                {profile?.status || "pending"}
              </span>
            </div>
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className={classes.card}>
            <div className="cardTitle">Discount Percentage</div>
            <div className="label">
              Sanaam Merchant Fee (Discount Percentage)
            </div>
            {formData.isEditingDiscount ? (
              <>
                <CommonInput
                  label=""
                  type="number"
                  placeholder="Enter discount percentage"
                  value={formData.discountPercentage}
                  onChange={(e) => setFormData((prev) => ({ ...prev, discountPercentage: e.target.value }))}
                  min={0}
                  max={100}
                  allowDecimal={true}
                />
                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                  <CommonButton
                    text="Save"
                    stylevariant="primary"
                    onClick={handleUpdateDiscount}
                    disabled={
                      !formData.discountPercentage ||
                      isNaN(formData.discountPercentage) ||
                      formData.discountPercentage < 0 ||
                      formData.discountPercentage > 100
                    }
                  />
                  <CommonButton
                    text="Cancel"
                    stylevariant="secondary2"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        isEditingDiscount: false,
                        discountPercentage: profile?.discount_percentage || "",
                      }));
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="value">
                  {profile?.settlement_charge_percentage !== undefined
                    ? `${profile.settlement_charge_percentage}%`
                    : "N/A"}
                </div>
                {/* <CommonButton
                  text="Edit"
                  stylevariant="secondary1"
                  onClick={() => setFormData((prev) => ({ ...prev, isEditingDiscount: true }))}
                  style={{ marginTop: "10px" }}
                /> */}
              </>
            )}
            <div
              style={{
                marginTop: "15px",
                fontSize: "12px",
                color: theme.colors.Violet,
                fontFamily: theme.fontFamily.MARKPRO,
              }}
            >
              Net amount = Gross amount - (Gross amount × (discount_percentage / 100))
            </div>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.card}>
            <div className="cardTitle">QR Code</div>
            <div className={classes.qrCodeContainer}>
              <div className="qrCode">
                {qrCode ? (
                  <img src={qrCode} alt="QR Code" />
                ) : (
                  <div
                    style={{
                      color: theme.colors.Violet,
                      fontFamily: theme.fontFamily.MARKPRO,
                    }}
                  >
                    Loading QR Code...
                  </div>
                )}
              </div>
              <div className="buttonGroup">
                <CommonButton
                  text="Download"
                  stylevariant="primary"
                  onClick={handleDownloadQR}
                  disabled={!qrCode}
                />
                <CommonButton
                  text="Print"
                  stylevariant="secondary1"
                  onClick={handlePrintQR}
                  disabled={!qrCode}
                />
              </div>
              {profile?.business_name && (
                <div
                  style={{
                    marginTop: "15px",
                    textAlign: "center",
                    color: theme.colors.Violet,
                    fontFamily: theme.fontFamily.MARKPROBOLD,
                  }}
                >
                  {profile.business_name}
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>

      <VendorProfileUpdateModal
        open={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        vendorProfile={vendorDashboardReducer?.vendorProfile}
      />
    </div>
  );
}

export default VendorSettings;
